/**
 * SORV Mission Control — Project & Agent Definitions (Frontend)
 */

export interface SorvAgent {
  id: string;
  name: string;
  role: string;
  trigger: string;
  autonomous: string[];
  limits: string[];
}

export interface SorvProject {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  agents: SorvAgent[];
}

export const SORV_PROJECTS: SorvProject[] = [
  {
    id: "orthoconnect",
    name: "OrthoConnect",
    description:
      "Sistema de adquisicion de pacientes para el Dr. Pedro Soriano Montiel (ortopedista)",
    color: "#00BCD4",
    icon: "hospital",
    agents: [
      {
        id: "ortho-whatsapp",
        name: "WhatsApp Conversation",
        role: "Cerebro detras de Orto. Monitorea conversaciones, aplica flujo CONVERSADOR, convierte en citas.",
        trigger: "Cada mensaje nuevo en WhatsApp Business",
        autonomous: [
          "Responder mensajes con prompt CONVERSADOR",
          "Crear citas en Cal.com via API",
          "Enviar confirmacion automatica",
          "Registrar lead en Notion",
        ],
        limits: ["No cancelar/mover citas confirmadas", "No cambiar precios en Cal.com"],
      },
      {
        id: "ortho-social",
        name: "Social Media Agent",
        role: "Mantiene presencia digital del doctor en Instagram, Facebook y Google Business.",
        trigger: "Scheduled: 1 post diario Instagram, 3x/semana Facebook",
        autonomous: [
          "Generar copy de posts",
          "Publicar en Instagram",
          "Responder comentarios",
          "Actualizar perfiles Google Business",
        ],
        limits: [
          "No publicar en Facebook hasta facebook_ready=true",
          "No publicar casos sin anonimizar",
        ],
      },
      {
        id: "ortho-analytics",
        name: "Analytics Agent",
        role: "Genera reporte diario de metricas y lo envia via Telegram + Notion.",
        trigger: "Scheduled: 8:00 PM diario",
        autonomous: [
          "Recopilar metricas WhatsApp",
          "Contar citas agendadas",
          "Calcular tasa de conversion",
          "Enviar reporte Telegram",
        ],
        limits: [],
      },
      {
        id: "ortho-escalation",
        name: "Escalation Agent",
        role: "Monitorea situaciones que requieren atencion humana inmediata.",
        trigger: "Event-driven (permanente)",
        autonomous: [
          "Detectar emergencias en mensajes",
          "Alertar mensajes sin respuesta >2h",
          "Detectar fallas en workflows n8n",
          "Alertar resenas negativas",
        ],
        limits: [],
      },
    ],
  },
  {
    id: "pageflow",
    name: "PageFlow",
    description:
      "Pipeline PDF-to-Video automatizado. Canal @animeresumidoesp. Contenido Kaiju No. 8.",
    color: "#FF6B35",
    icon: "film",
    agents: [
      {
        id: "pf-pipeline",
        name: "Pipeline Agent",
        role: "Orquestador del flujo completo PDF a Video.",
        trigger: "Notion Queue polling cada 30 min + webhook n8n",
        autonomous: [
          "Detectar PDF en cola",
          "Extraer texto y frames",
          "Generar script narracion",
          "Enviar a ElevenLabs/KIE.AI",
          "Combinar en Creatomate",
          "Mover a cola publicacion",
        ],
        limits: [],
      },
      {
        id: "pf-publisher",
        name: "Publisher Agent",
        role: "Gestiona publicacion diaria en YouTube con SEO optimizado.",
        trigger: "Scheduled: 17:45 MX diario",
        autonomous: [
          "Tomar siguiente video de cola",
          "Generar titulo SEO",
          "Generar descripcion y thumbnail",
          "Subir a YouTube via API",
        ],
        limits: [],
      },
      {
        id: "pf-quality",
        name: "Quality Agent",
        role: "Revisa cada video producido antes de publicacion.",
        trigger: "Event: cuando Pipeline marca video como Producido",
        autonomous: [
          "Verificar duracion (4-20 min)",
          "Verificar sincronizacion audio",
          "Verificar resolucion imagenes",
          "Re-procesar pasos fallidos (max 2 reintentos)",
        ],
        limits: [],
      },
      {
        id: "pf-analytics",
        name: "Analytics Agent",
        role: "Monitorea rendimiento de canales y genera reportes semanales.",
        trigger: "Scheduled: Lunes 9:00 AM",
        autonomous: [
          "Rastrear views/watch time/suscriptores",
          "Monitorear creditos KIE.AI",
          "Generar reporte Notion + Telegram",
        ],
        limits: [],
      },
    ],
  },
  {
    id: "realestate",
    name: "Real Estate SORV",
    description:
      "Brazo de inversion inmobiliaria. Wholesaling, land flipping, BRRRR y coliving en Puebla.",
    color: "#4CAF50",
    icon: "building",
    agents: [
      {
        id: "re-deal-scout",
        name: "Deal Scout Agent",
        role: "Busca propiedades con potencial en portales y fuentes off-market.",
        trigger: "Scheduled: 2 veces al dia (9 AM y 6 PM)",
        autonomous: [
          "Monitorear Inmuebles24/Vivanuncios/ML",
          "Filtrar por estrategia (wholesaling/BRRRR/etc)",
          "Registrar deals en Notion con score",
        ],
        limits: [],
      },
      {
        id: "re-market-intel",
        name: "Market Intel Agent",
        role: "Monitorea tendencias del mercado inmobiliario en Puebla.",
        trigger: "Scheduled: Miercoles y Domingo",
        autonomous: [
          "Recopilar precios por m2 por zona",
          "Rastrear proyectos nuevos",
          "Monitorear tasas hipotecarias",
          "Generar informe semanal",
        ],
        limits: [],
      },
      {
        id: "re-financial",
        name: "Financial Analyzer Agent",
        role: "Analiza viabilidad financiera de deals con score >= 7.",
        trigger: "Event: cuando Deal Scout registra deal score >= 7",
        autonomous: [
          "Calcular ARV/MAO para wholesaling",
          "Calcular cap rate/cashflow para BRRRR",
          "Generar ficha completa en Notion",
          "Emitir recomendacion COMPRAR/PASAR/INVESTIGAR",
        ],
        limits: [],
      },
      {
        id: "re-document",
        name: "Document Manager Agent",
        role: "Organiza documentacion de prospectos, due diligence y transacciones.",
        trigger: "Event: cuando deal pasa a En negociacion",
        autonomous: [
          "Crear carpeta en Notion",
          "Generar checklist due diligence",
          "Generar borradores LOI",
          "Rastrear fechas criticas",
        ],
        limits: [],
      },
      {
        id: "re-network",
        name: "Network Agent",
        role: "Gestiona seguimiento de contactos estrategicos del ecosistema inmobiliario.",
        trigger: "Scheduled: Viernes (revision semanal)",
        autonomous: [
          "Generar recordatorios de seguimiento",
          "Redactar mensajes de follow-up",
          "Registrar interacciones en Notion",
        ],
        limits: [],
      },
      {
        id: "re-leads",
        name: "Leads Agent",
        role: "Genera y nutre leads de vendedores motivados para wholesaling.",
        trigger: "Scheduled: Diario 10:00 AM",
        autonomous: [
          "Scraping de anuncios con senales de vendedor motivado",
          "Preparar scripts de llamada",
          "Seguimiento automatico leads tibios cada 72h",
        ],
        limits: [],
      },
    ],
  },
  {
    id: "fitness",
    name: "💪 Fitness & Nutrición",
    description:
      "Sistema integrado de entrenamiento, nutricion y suplementacion personalizado para Pedro. Progressive overload automatico basado en InBody, plan del nutriologo Ln. Jorge Velazquez Meza, y equipo exacto de Club OMADA Lomas Angelopolis.",
    color: "#E91E63",
    icon: "💪",
    agents: [
      {
        id: "workout-coach",
        name: "Workout Coach",
        role: "Genera la rutina del dia segun el split semanal aplicando progressive overload basado en el log de la ultima sesion del mismo dia. Detecta si esta en semana de deload (semana 5 del mesociclo). En deload: reduce volumen 40-50% e intensidad. Envia rutina por Telegram con: ejercicio, series, reps, peso sugerido, RPE meta, tempo y tiempo de descanso.",
        trigger: "Scheduled L-V, 30 min antes del gym",
        autonomous: [
          "Generar rutina del dia con progressive overload",
          "Detectar semana de deload (semana 5 mesociclo)",
          "Consultar log de ultima sesion del mismo dia",
          "Enviar rutina por Telegram",
        ],
        limits: [],
      },
      {
        id: "log-tracker",
        name: "Log Tracker",
        role: "Recibe y parsea el reporte post-entreno de Pedro (formato libre o estructurado). Registra en Notion: fecha, ejercicio, sets, reps, peso, RPE, grupo muscular, notas. Calcula volumen del dia por grupo muscular. Aplica algoritmo de overload para determinar si se sube peso la proxima sesion. Confirma por Telegram con resumen y proyeccion de la proxima sesion.",
        trigger: "Event: cuando Pedro reporta entreno por Telegram",
        autonomous: [
          "Parsear reporte post-entreno",
          "Registrar sesion en Notion",
          "Calcular volumen por grupo muscular",
          "Aplicar algoritmo de progressive overload",
        ],
        limits: [],
      },
      {
        id: "nutricion-monitor",
        name: "Nutrición Monitor",
        role: "Envia recordatorio de cada comida por Telegram con el menu exacto del plan del nutriologo Ln. Jorge Velazquez Meza. Si Pedro confirma, registra adherencia en Notion. Si no responde en 30 min, envia re-recordatorio suave. Lunes AM reporta % de adherencia semanal. KPI meta: >=80% adherencia semanal.",
        trigger: "Scheduled 8:00/10:30/15:00/18:00/22:00",
        autonomous: [
          "Enviar recordatorio de comida por Telegram",
          "Registrar adherencia en Notion",
          "Re-recordatorio si no responde en 30 min",
          "Reporte semanal de adherencia lunes AM",
        ],
        limits: [],
      },
      {
        id: "suplementos-coach",
        name: "Suplementos Coach",
        role: "Recordatorio AM: Vitamina C + Nootropico con desayuno. Recordatorio PM: Creatina + Ashwagandha con cena, y a las 11:00 PM: Magnesio + Zinc solos, a las 11:30 PM: Melatonina. Registra adherencia al stack. Alerta si Pedro combina Vitamina C con Zinc (son incompatibles). Reporte semanal de adherencia al protocolo.",
        trigger: "Scheduled 8:00 AM y 10:00 PM diario",
        autonomous: [
          "Enviar recordatorio AM (Vitamina C + Nootropico)",
          "Enviar recordatorio PM (Creatina + Ashwagandha + Mg/Zn + Melatonina)",
          "Alertar si combina Vitamina C con Zinc",
          "Registrar adherencia al stack",
        ],
        limits: [],
      },
      {
        id: "weekly-analyzer",
        name: "Weekly Analyzer",
        role: "Analiza la semana anterior: volumen real vs meta por grupo muscular, progresion de pesos, dias entrenados vs planificados, adherencia nutricional y de suplementos, indice de fatiga acumulada (RPE promedio ultimas 4 semanas). Decide si la proxima semana es deload. Si detecta meseta de 3+ semanas, alerta a Pedro con sugerencia de ajuste de programa. Genera reporte en Notion + resumen Telegram max 10 lineas.",
        trigger: "Scheduled Lunes 8:00 AM",
        autonomous: [
          "Analizar volumen real vs meta por grupo muscular",
          "Evaluar progresion de pesos e indice de fatiga",
          "Decidir si proxima semana es deload",
          "Generar reporte Notion + resumen Telegram",
        ],
        limits: [],
      },
      {
        id: "inbody-tracker",
        name: "InBody Tracker",
        role: "Procesa los resultados del InBody de Pedro. Compara con el historial (Sep 2025: 88.1kg 22.3%, Oct 2025: 89.9kg 22.5%, Ene 2026: 94.8kg 28.5%). Calcula delta de grasa y musculo. Detecta si va en la direccion correcta hacia el objetivo (79.7 kg, -15.1 kg grasa). Ajusta las metas de volumen semanal segun los nuevos datos segmentales. Genera reporte visual en Notion con tendencias y recomendaciones para el siguiente mes.",
        trigger: "Event: cuando Pedro sube nuevo InBody o mensual",
        autonomous: [
          "Procesar resultados InBody",
          "Comparar con historial y objetivo (79.7 kg)",
          "Ajustar metas de volumen segun datos segmentales",
          "Generar reporte visual en Notion con tendencias",
        ],
        limits: [],
      },
    ],
  },
  {
    id: "cuidado-personal",
    name: "💆 Cuidado Personal",
    description:
      "Sistema de rutinas de cuidado personal AM/PM con inventario de productos, recordatorios de rutinas semanales y mensuales, y tracking de adherencia. Objetivo: glow up consistente con 30 min AM + 30 min PM diarios.",
    color: "#9C27B0",
    icon: "💆",
    // Desde el 23-sep-2026 lo lleva Apolo. Aqui habia cuatro agentes
    // (rutina-am-coach, rutina-pm-coach, weekly-routine-coach y
    // adherencia-tracker) cuyos ids ya no disparaba ningun schedule, asi que el
    // interruptor de su tarjeta en Mission Control no apagaba nada. Cada id de
    // aqui es el agentId de un schedule de server/services/agentSchedules.js.
    // Los textos no nombran productos: la rutina se arma con la de hoy, sin lo
    // pausado ni lo agotado, y una lista escrita aqui se quedaria vieja.
    agents: [
      {
        id: "apolo-am",
        name: "Apolo · Rutina AM",
        role: "Manda por Telegram la rutina AM de hoy, armada con el inventario real: quita lo pausado y lo agotado, resuelve las incompatibilidades entre productos y dice que quito y por que. No pide confirmar nada; si no hay nada que decir, no manda mensaje.",
        trigger: "Scheduled 6:45 AM diario",
        autonomous: [
          "Armar la rutina AM de hoy desde el inventario",
          "Quitar lo pausado y lo agotado, y decirlo",
          "Resolver incompatibilidades entre productos",
        ],
        limits: ["No pide confirmacion de cada paso"],
      },
      {
        id: "apolo-pm",
        name: "Apolo · Rutina PM",
        role: "Manda por Telegram la rutina PM de hoy con lo semanal que toque ese dia, armada con el inventario real y con las incompatibilidades ya resueltas. No pide confirmar nada; si no hay nada que decir, no manda mensaje.",
        trigger: "Scheduled 10:25 PM diario",
        autonomous: [
          "Armar la rutina PM de hoy desde el inventario",
          "Anadir lo semanal que toque ese dia",
          "Resolver incompatibilidades entre productos",
        ],
        limits: ["No pide confirmacion de cada paso"],
      },
      {
        id: "apolo-inventario",
        name: "Apolo · Lo que se acaba",
        role: "Avisa de los productos que se estan acabando o que Pedro marco como agotados, para comprarlos. Solo manda mensaje si de verdad hay algo.",
        trigger: "Scheduled jueves 7:00 PM",
        autonomous: ["Listar lo que se acaba o ya se acabo"],
        limits: [],
      },
      {
        id: "apolo-citas",
        name: "Apolo · Citas",
        role: "Avisa de las citas que ya tocan (barbero, cejas, dentista) con lo que van a costar, y de las que siguen pendientes de agendar. Solo manda mensaje si alguna toca.",
        trigger: "Scheduled lunes 7:00 PM",
        autonomous: ["Listar las citas que ya tocan y su costo"],
        limits: [],
      },
      {
        id: "apolo-repaso",
        name: "Apolo · Repaso semanal",
        role: "Repaso del domingo: cuanto hay por reponer, que citas tocan o faltan por agendar, y una sola pregunta abierta sobre la semana. No es un formulario.",
        trigger: "Scheduled domingo 8:00 PM",
        autonomous: ["Enviar el repaso semanal por Telegram"],
        limits: [],
      },
    ],
  },
  {
    id: "marca-personal",
    name: "🧑‍💼 Marca Personal",
    description:
      "Sistema de gestion y crecimiento de la marca profesional de Pedro Soriano Vergara. Empresario, disciplinado, proyectos, aprendizaje continuo. Separado de EssEntial (hobbies). Prioridad 2026: media-baja, mantenimiento y crecimiento organico.",
    color: "#FF9800",
    icon: "🧑‍💼",
    agents: [
      {
        id: "content-documenter",
        name: "Content Documenter",
        role: "Cuando Pedro menciona por Telegram un nuevo evento, curso, certificacion o logro, genera automaticamente el copy para Instagram y LinkedIn (tono profesional, conciso, con CTA hacia pedrosorianovergara.com). Registra el contenido pendiente en Notion. Pregunta a Pedro si quiere publicarlo ya o guardarlo en cola. Sigue la filosofia: documentar lo que ya haces, no crear contenido artificial.",
        trigger: "Event: cuando Pedro reporta un logro, evento o curso",
        autonomous: [
          "Generar copy para Instagram y LinkedIn",
          "Registrar contenido pendiente en Notion",
          "Preguntar a Pedro si publicar o guardar en cola",
          "Mantener filosofia de documentar, no crear",
        ],
        limits: [],
      },
      {
        id: "social-monitor",
        name: "Social Monitor",
        role: "Revisa el estado semanal de la marca personal. Reporta: dias desde ultima publicacion en Instagram, contenido pendiente en cola de Notion, hitos del trimestre completados vs pendientes. Si han pasado mas de 30 dias sin publicar en Instagram, envia alerta a Pedro por Telegram con el contenido pendiente mas relevante listo para publicar. Nunca presiona — solo informa.",
        trigger: "Scheduled Lunes 9:00 AM (reporte semanal)",
        autonomous: [
          "Reportar dias desde ultima publicacion Instagram",
          "Listar contenido pendiente en cola Notion",
          "Alertar si >30 dias sin publicar",
          "Informar hitos trimestrales completados vs pendientes",
        ],
        limits: [],
      },
      {
        id: "web-tracker",
        name: "Web Tracker",
        role: "Hace seguimiento del progreso de pedrosorianovergara.com. Revisa el checklist en Notion: dominio, ATHENEA, PageFlow pendiente, EssEntial pendiente, diseno final pendiente, lanzamiento v1.0 pendiente. Reporta % de avance y el siguiente paso concreto a ejecutar. Cuando el sitio llegue al 100%, genera el anuncio de lanzamiento para todas las plataformas.",
        trigger: "Scheduled primer lunes de cada mes",
        autonomous: [
          "Revisar checklist de progreso web en Notion",
          "Reportar % avance y siguiente paso concreto",
          "Generar anuncio de lanzamiento cuando web llegue a 100%",
          "Actualizar status en Notion",
        ],
        limits: [],
      },
      {
        id: "linkedin-coach",
        name: "LinkedIn Coach",
        role: "Guia a Pedro paso a paso en la configuracion de LinkedIn. Genera textos optimizados para: headline (empresario + estudiante ADE Anahuac + fundador SORV Capital), seccion Acerca de (menciona OrthoConnect, PageFlow, Real Estate, FIFA Voluntario 2026, Fudan Shanghai FISS), experiencia, y habilidades clave. Una vez configurado, monitorea oportunidades de networking con contactos del sector inmobiliario y tecnologico en Puebla/Mexico.",
        trigger: "Event: activado por Pedro cuando quiere trabajar LinkedIn",
        autonomous: [
          "Generar textos optimizados para perfil LinkedIn",
          "Guiar configuracion paso a paso",
          "Monitorear oportunidades de networking",
          "Sugerir conexiones estrategicas del sector",
        ],
        limits: [],
      },
    ],
  },
  {
    id: "financiero",
    name: "💰 Sistema Financiero",
    description:
      "Sistema de monitoreo y control financiero personal de Pedro. 4 tarjetas de credito con deuda total ~$152,618 MXN. Flujo semanal $8,000 MXN. Meta: reducir uso de credito de 91% a 75% y eliminar deuda Banamex (tasa 62.94% anual).",
    color: "#2196F3",
    icon: "💰",
    agents: [
      {
        id: "debt-monitor",
        name: "Debt Monitor",
        role: "Cada lunes reporta por Telegram el estado actual de las 4 tarjetas: saldo, limite, % de uso, disponible y fecha de pago proximo. Calcula el uso total de credito y lo compara con la meta del 75%. Alerta si alguna tarjeta supera el 90% de uso o esta en sobregiro. Registra el snapshot semanal en Notion para tracking historico.",
        trigger: "Scheduled Lunes 7:00 AM (reporte semanal)",
        autonomous: [
          "Reportar estado de 4 tarjetas por Telegram",
          "Calcular uso total de credito vs meta 75%",
          "Alertar si tarjeta supera 90% o sobregiro",
          "Registrar snapshot semanal en Notion",
        ],
        limits: [],
      },
      {
        id: "payment-planner",
        name: "Payment Planner",
        role: "Con el flujo semanal de $8,000 MXN, genera el plan de pagos optimo para la semana priorizando: 1. Banamex (tasa 62.94%), 2. Nu (evitar cargos por limite topado), 3. BBVA (resolver sobregiro), 4. Hey Banco (pago minimo si falta presupuesto). Envia el plan por Telegram con montos exactos por tarjeta. Registra en Notion base de datos Plan de Pagos Semanales.",
        trigger: "Scheduled Lunes 7:30 AM (despues del Debt Monitor)",
        autonomous: [
          "Generar plan de pagos semanal optimo",
          "Priorizar Banamex (62.94% tasa) y Nu (99.3%)",
          "Enviar plan por Telegram con montos exactos",
          "Registrar plan en Notion",
        ],
        limits: [],
      },
      {
        id: "subscriptions-auditor",
        name: "Subscriptions Auditor",
        role: "Audita las suscripciones activas ($9,226/mes total). Identifica cuales se pueden cancelar o pausar para liberar flujo. Las suscripciones actuales superan el flujo mensual — esto es critico. Genera reporte en Notion con: nombre, costo, fecha de cobro, tarjeta asociada, y recomendacion (mantener/cancelar/pausar). Envia resumen a Pedro por Telegram con el ahorro potencial.",
        trigger: "Scheduled primer lunes de cada mes",
        autonomous: [
          "Auditar suscripciones activas ($9,226/mes)",
          "Identificar suscripciones para cancelar o pausar",
          "Generar reporte en Notion con recomendaciones",
          "Enviar resumen Telegram con ahorro potencial",
        ],
        limits: [],
      },
      {
        id: "financial-alert",
        name: "Financial Alert",
        role: "Alerta inmediata a Pedro por Telegram cuando: una tarjeta supera el 95% de uso, se detecta un cargo no reconocido reportado por Pedro, la suma de pagos minimos supera $8,000 (flujo semanal), Banamex no recibe pago en mas de 7 dias, o el uso total de credito sube en lugar de bajar. Formato: FINANCIERO | [TIPO] | [Detalle] | [Accion sugerida inmediata].",
        trigger: "Event-driven: monitoreo permanente",
        autonomous: [
          "Alertar si tarjeta supera 95% de uso",
          "Detectar pagos minimos > flujo semanal",
          "Alertar si Banamex sin pago >7 dias",
          "Alertar si uso total de credito sube",
        ],
        limits: [],
      },
    ],
  },
  {
    id: "lectura",
    name: "📚 Lectura & PageFlow",
    description:
      "Sistema de lectura diaria personal integrado con el pipeline PageFlow. Cada libro leido fisicamente → escaneo → PDF → pipeline de videos para YouTube. Meta: 20 paginas/dia, ~2.4 libros/mes, ~29 libros/ano. Lista de ~155 libros.",
    color: "#00BCD4",
    icon: "📚",
    agents: [
      {
        id: "reading-coach",
        name: "Reading Coach",
        role: "Recordatorio diario de lectura por Telegram: Ya leiste hoy? 20 paginas de [libro actual]. Si Pedro confirma, registra el dia en el streak de Notion. Si no responde en 30 min, envia recordatorio suave. Lunes reporta streak semanal (dias leidos / 7) y progreso de paginas en el libro actual. Cuando Pedro reporta terminar un libro, genera reporte y sugiere el siguiente de la cola priorizada.",
        trigger: "Scheduled 9:00 PM diario",
        autonomous: [
          "Enviar recordatorio diario de lectura por Telegram",
          "Registrar streak en Notion",
          "Reportar streak semanal los lunes",
          "Sugerir siguiente libro al terminar uno",
        ],
        limits: [],
      },
      {
        id: "pageflow-queue",
        name: "PageFlow Queue",
        role: "Gestiona la cola de libros listos para entrar al pipeline PageFlow. Cuando Pedro reporta que escaneo un libro, registra en Notion con status Pendiente produccion. Cuando PageFlow se reactive (abril 2026), notifica a Pedro la cola completa con libros listos para procesar. Estima cuantos videos va a generar cada libro (basado en capitulos) y el tiempo de produccion estimado.",
        trigger: "Event: cuando Pedro termina un libro y lo escanea",
        autonomous: [
          "Registrar libro escaneado en Notion",
          "Estimar videos por libro basado en capitulos",
          "Notificar cola completa cuando PageFlow se reactive",
          "Calcular tiempo de produccion estimado",
        ],
        limits: [],
      },
      {
        id: "book-insights",
        name: "Book Insights",
        role: "Cuando Pedro reporta terminar un libro, genera un resumen ejecutivo en Notion con: titulo, autor, 3-5 ideas clave, citas memorables, aplicaciones practicas a los proyectos SORV actuales (OrthoConnect, PageFlow, Real Estate), y rating personal de Pedro (1-10). Envia resumen compacto por Telegram. Registra en el historial de lectura 2026.",
        trigger: "Event: cuando Pedro termina un libro",
        autonomous: [
          "Generar resumen ejecutivo en Notion",
          "Extraer ideas clave y citas memorables",
          "Conectar aplicaciones practicas a proyectos SORV",
          "Enviar resumen compacto por Telegram",
        ],
        limits: [],
      },
      {
        id: "reading-analyst",
        name: "Reading Analyst",
        role: "Reporte mensual de lectura: libros completados en el mes, paginas leidas totales, streak promedio (% dias leidos), progreso vs meta anual (12 libros/ano), videos generados en PageFlow ese mes, proyeccion para completar la lista de 155 libros al ritmo actual. Si el ritmo esta por debajo de la meta, sugiere ajuste de paginas/dia. Envia reporte a Pedro por Telegram + registra en Notion.",
        trigger: "Scheduled primer lunes de cada mes",
        autonomous: [
          "Reportar libros completados y paginas leidas",
          "Calcular progreso vs meta anual (12 libros)",
          "Proyectar tiempo para completar lista de 155 libros",
          "Sugerir ajuste de paginas/dia si ritmo esta bajo",
        ],
        limits: [],
      },
    ],
  },
  {
    id: "bienestar",
    name: "🤝 Relaciones & Bienestar",
    description:
      "Sistema de bienestar integral: control de energia, rutinas de respiracion y ejercicio de suelo pelvico, stack de suplementos especifico, circulo social activo y entorno fisico optimizado. Filosofia: Control → Confianza → Mejor experiencia.",
    color: "#FF5722",
    icon: "🤝",
    agents: [
      {
        id: "wellness-am-coach",
        name: "Wellness AM Coach",
        role: "Envia checklist de rutina AM de bienestar por Telegram: Kegel rapidos + respiracion diafragmatica + suplementos Zinc y Vitamina D. Si Pedro confirma, registra en Notion. Recuerda que Zinc AM no es compatible con Vitamina C AM (tomarlos separados al menos 2 horas). Lunes reporta adherencia semanal AM.",
        trigger: "Scheduled 7:30 AM diario (despues de rutina skincare AM)",
        autonomous: [
          "Enviar checklist AM bienestar por Telegram",
          "Registrar adherencia en Notion",
          "Alertar incompatibilidad Zinc + Vitamina C",
          "Reportar adherencia semanal AM los lunes",
        ],
        limits: [],
      },
      {
        id: "wellness-pm-coach",
        name: "Wellness PM Coach",
        role: "Envia checklist de rutina PM por Telegram: Kegel rapidos + respiracion 4-7-8 + suplementos Magnesio y Ashwagandha. Si es lunes, miercoles o viernes, recuerda la sesion de control semanal si aun no se completo. Registra adherencia en Notion. KPI meta: >=85% dias con rutina PM completa.",
        trigger: "Scheduled 10:45 PM diario (despues de rutina skincare PM)",
        autonomous: [
          "Enviar checklist PM bienestar por Telegram",
          "Recordar sesion de control L/Mi/V si pendiente",
          "Registrar adherencia en Notion",
          "Calcular % dias con rutina PM completa",
        ],
        limits: [],
      },
      {
        id: "weekly-sessions-tracker",
        name: "Weekly Sessions Tracker",
        role: "Lunes, miercoles y viernes a las 8 PM: recordatorio de sesion de control semanal (15-20 min). Incluye el protocolo completo: respiracion, stop-start, kegel activo. Si Pedro confirma, registra en Notion. Lunes 9 AM: reporte semanal completo de KPIs: dias sin porno, sesiones Kegel, sesiones de control, minutos de respiracion, adherencia suplementos y planes sociales. Compara con semana anterior.",
        trigger: "Scheduled L/Mi/V 8:00 PM + reporte Lunes 9:00 AM",
        autonomous: [
          "Recordatorio sesion de control L/Mi/V 8 PM",
          "Registrar sesiones completadas en Notion",
          "Generar reporte semanal completo de KPIs lunes AM",
          "Comparar KPIs con semana anterior",
        ],
        limits: [],
      },
      {
        id: "social-circle-tracker",
        name: "Social Circle Tracker",
        role: "Viernes PM: pregunta a Pedro si tiene planes sociales el fin de semana con personas que sumen (amigos, networking, familia). Si no tiene nada planeado, sugiere retomar contacto con alguien de su circulo. Registra en Notion las interacciones sociales de la semana. Lunes: incluye en el reporte semanal el score de circulo social (planes realizados vs meta 1-2/semana). Alerta si han pasado 2+ semanas sin ningun plan social.",
        trigger: "Scheduled Viernes 6:00 PM (planificacion fin de semana)",
        autonomous: [
          "Preguntar planes sociales viernes PM",
          "Sugerir retomar contacto si no hay planes",
          "Registrar interacciones sociales en Notion",
          "Alertar si 2+ semanas sin plan social",
        ],
        limits: [],
      },
    ],
  },
  {
    id: "universidad",
    name: "🎓 Universidad",
    description:
      "Seguimiento academico de la Licenciatura en Administracion y Direccion de Empresas en Universidad Anahuac Puebla. Matricula 00441251. PGA 8.02. 213/357 creditos (59.7%). Graduacion proyectada: diciembre 2028.",
    color: "#607D8B",
    icon: "🎓",
    agents: [
      {
        id: "academic-monitor",
        name: "Academic Monitor",
        role: "Reporte semanal academico por Telegram: materias activas del semestre, tareas o entregas importantes de la semana, recordatorio de fechas criticas (examenes, entregas finales). Monitorea el avance en creditos: 213/357 actuales, meta de 24 cr/semestre para graduacion Dic 2028. Alerta si alguna materia tiene riesgo de reprobacion basado en lo que Pedro reporta.",
        trigger: "Scheduled Lunes 8:00 AM durante semestres activos",
        autonomous: [
          "Reportar materias activas y entregas de la semana",
          "Recordar fechas criticas (examenes, entregas finales)",
          "Monitorear avance en creditos 213/357",
          "Alertar si materia en riesgo de reprobacion",
        ],
        limits: [],
      },
      {
        id: "fudan-tracker",
        name: "Fudan Shanghai Tracker",
        role: "Monitorea el proceso de admision al programa Fudan FISS Shanghai (Jul 13 - Ago 7, 2026). Deadline critico: 13 abril 2026 (1er batch). Documentos requeridos: 1) transcript en ingles via Cathleen Pomaski, 2) carta de inscripcion en ingles via veronica.bonilla@anahuac.mx, 3) verificacion de pasaporte. Codigo de admision: 853942. Envia recordatorio diario del documento pendiente mas urgente hasta que Pedro confirme que todos estan enviados.",
        trigger: "Scheduled diario hasta el 13 abril 2026, luego mensual",
        autonomous: [
          "Recordatorio diario de documento pendiente mas urgente",
          "Rastrear status de transcript, carta y pasaporte",
          "Alertar deadline 13 abril 2026 (1er batch)",
          "Confirmar cuando todos los documentos estan enviados",
        ],
        limits: [],
      },
      {
        id: "requirements-tracker",
        name: "Requirements Tracker",
        role: "Monitorea el avance en requisitos especiales de graduacion: materias en ingles (necesita 2 mas), materias en linea (necesita 3 mas), practicas profesionales (480 horas, en 0), servicio social (pendiente), EGEL (al final). Genera reporte mensual con semaforo de cada requisito y el siguiente paso concreto para avanzar en el mas urgente. Alerta cuando se acerque el periodo para inscribir materias del siguiente semestre.",
        trigger: "Scheduled primer lunes de cada mes",
        autonomous: [
          "Reportar semaforo de requisitos de graduacion",
          "Rastrear materias en ingles (faltan 2) y en linea (faltan 3)",
          "Monitorear practicas profesionales (480h, en 0)",
          "Alertar periodo de inscripcion del siguiente semestre",
        ],
        limits: [],
      },
      {
        id: "schedule-planner",
        name: "Schedule Planner",
        role: "Al inicio de cada semestre, ayuda a Pedro a planificar la carga academica optima. Considera: creditos faltantes por semestre, materias disponibles ese periodo, compatibilidad de horarios con compromisos de SORV Capital y proyectos. Prioriza materias que mas atrasan la graduacion. Genera el horario propuesto para revision de Pedro antes de inscribir.",
        trigger: "Event: activado por Pedro al inicio de cada semestre",
        autonomous: [
          "Planificar carga academica optima del semestre",
          "Verificar compatibilidad con horarios SORV Capital",
          "Priorizar materias que mas atrasan graduacion",
          "Generar horario propuesto para revision",
        ],
        limits: [],
      },
    ],
  },
  {
    id: "sorv-alpha",
    name: "SORV-Alpha",
    description: "Polymarket Intelligence & Trading Engine — autonomous OSINT-driven prediction market trading",
    color: "#00BCD4",
    icon: "brain-circuit",
    agents: [
      {
        id: "alpha-intelligence",
        name: "Intelligence",
        role: "Recolecta senales OSINT de GDELT, RSS feeds (BBC, NPR, Al Jazeera, CoinDesk, CoinTelegraph)",
        trigger: "Cada 60 segundos — ciclo continuo 24/7",
        autonomous: ["Fetch GDELT events", "Fetch RSS feeds", "Deduplicar senales", "Filtrar por sentiment VADER"],
        limits: [],
      },
      {
        id: "alpha-analyzer",
        name: "Analyzer",
        role: "Analiza senales con Claude Sonnet, mapea a mercados Polymarket, estima probabilidades",
        trigger: "Event — cuando Intelligence detecta senal con sentiment fuerte",
        autonomous: ["Llamar Claude API para decision de trading", "Keyword matching a mercados", "Calcular Kelly Criterion"],
        limits: ["Max 3 mercados por senal", "Min 5% edge para considerar trade"],
      },
      {
        id: "alpha-executor",
        name: "Executor",
        role: "Ejecuta paper trades (Fase 1-2) o live trades (Fase 3+) en Polymarket",
        trigger: "Event — cuando Analyzer aprueba trade con edge positivo",
        autonomous: ["Ejecutar paper trades", "Registrar en SQLite", "Calcular P&L"],
        limits: ["Max $50 por trade", "Max 10% del bankroll", "Solo paper mode hasta Fase 3"],
      },
      {
        id: "alpha-risk",
        name: "Risk Manager",
        role: "Valida cada trade con 8 checks de riesgo antes de ejecucion",
        trigger: "Event — antes de cada trade",
        autonomous: ["Validar tamanio de posicion", "Verificar edge minimo", "Verificar liquidez del mercado"],
        limits: ["Daily stop loss $30", "Max 15 trades/dia", "Max 8 posiciones abiertas"],
      },
      {
        id: "alpha-circuit-breaker",
        name: "Circuit Breaker",
        role: "Pausa automaticamente el trading si detecta anomalias",
        trigger: "Cada 60 segundos + event-driven en cada trade",
        autonomous: ["Pausar trading en perdidas consecutivas", "Alertar si no hay senales >30min"],
        limits: [],
      },
      {
        id: "alpha-coordinator",
        name: "Coordinator",
        role: "Orquestador principal — 6 loops async corriendo en paralelo 24/7",
        trigger: "Arranque de PM2 — corre indefinidamente",
        autonomous: ["Intelligence loop", "Market refresh", "Equity snapshots", "Daily summary", "Data cleanup"],
        limits: [],
      },
    ],
  },
];

export function getAllAgents() {
  return SORV_PROJECTS.flatMap((p) =>
    p.agents.map((a) => ({
      ...a,
      projectId: p.id,
      projectName: p.name,
      projectColor: p.color,
    })),
  );
}

export function getProjectById(id: string) {
  return SORV_PROJECTS.find((p) => p.id === id) ?? null;
}
